import {
  layoutSpec,
  luminance,
  readableOn,
  offeredLayoutOptions,
  STUDIO_FINISH_OPTIONS,
  resolvedFinish,
  resolvedLayoutOptions,
  storedStyleOf,
  studioOf,
  studioTheme,
  themeContrastWarnings,
  type ContrastWarning,
  type FinishOptionKey,
  type FinishOptions,
  type LayoutOptionKey,
  type LayoutOptions,
  type StudioChoice,
  type StyleChoice,
  type ThemeFile,
} from '@base/shared';

/**
 * Regras do Estúdio de estilos (ADR 0128, fase 5), sem React, para os testes. O estúdio é
 * controlado: recebe a escolha e devolve a nova; quem o usa (Configuração › Estilo aqui, a
 * entrevista no Smart Builder) decide onde guardar.
 */

export const STUDIO_STEPS = ['layout', 'color', 'finish', 'review'] as const;
export type StudioStep = (typeof STUDIO_STEPS)[number];

/** O de fábrica do beadev: lateral clássica, paleta Beadev, acabamento Sóbrio. */
export const BEADEV_STUDIO: StudioChoice = studioOf({ style: 'beadev' })!;

/** Por onde o estúdio começa: o estilo escolhido; senão o de fábrica; senão o do beadev. */
export function initialStudio(
  saved: StyleChoice | null,
  factory: StyleChoice | null,
): StudioChoice {
  return (saved && studioOf(saved)) ?? (factory && studioOf(factory)) ?? BEADEV_STUDIO;
}

/** Trocar de layout começa dos ajustes padrão dele (os de outro layout não valem aqui). */
export function withLayout(s: StudioChoice, id: string): StudioChoice {
  if (!layoutSpec(id) || s.layout.id === id) return s;
  return { ...s, layout: { id } };
}

export function withLayoutOption<K extends LayoutOptionKey>(
  s: StudioChoice,
  key: K,
  value: LayoutOptions[K],
): StudioChoice {
  return { ...s, layout: { ...s.layout, options: { ...s.layout.options, [key]: value } } };
}

/** Trocar de paleta tira a cor de destaque trocada e os ajustes de cor da anterior. */
export function withPalette(s: StudioChoice, id: string, logoColors?: string[]): StudioChoice {
  return { ...s, palette: { id, ...(id === 'logo' && logoColors && { logoColors }) } };
}

export function withAccent(s: StudioChoice, accent: string | undefined): StudioChoice {
  const { accent: _old, ...rest } = s.palette;
  return { ...s, palette: accent ? { ...rest, accent } : rest };
}

export type PaletteColorKey = keyof NonNullable<StudioChoice['palette']['colors']>;

/** Uma cor da paleta trocada (ou de volta à da paleta, sem valor). */
export function withColor(
  s: StudioChoice,
  key: PaletteColorKey,
  color: string | undefined,
): StudioChoice {
  const { [key]: _old, ...rest } = s.palette.colors ?? {};
  const colors = color ? { ...rest, [key]: color } : rest;
  const { colors: _c, ...palette } = s.palette;
  return {
    ...s,
    palette: Object.keys(colors).length ? { ...palette, colors } : palette,
  };
}

/** Trocar de acabamento começa dos valores dele. */
export function withFinish(s: StudioChoice, id: string): StudioChoice {
  if (s.finish.id === id) return s;
  return { ...s, finish: { id } };
}

export function withFinishOption<K extends FinishOptionKey>(
  s: StudioChoice,
  key: K,
  value: FinishOptions[K],
): StudioChoice {
  return { ...s, finish: { ...s.finish, options: { ...s.finish.options, [key]: value } } };
}

/** Os valores que valem agora: os do layout e do acabamento com os ajustes por cima. */
export function resolvedStudio(s: StudioChoice): { layout: LayoutOptions; finish: FinishOptions } {
  return {
    layout: resolvedLayoutOptions(s.layout.id, s.layout.options),
    finish: resolvedFinish(s.finish.id, s.finish.options) ?? resolvedFinish('sobrio')!,
  };
}

/** Duas escolhas dão o mesmo sistema: mesmos ids, mesmas cores e os mesmos valores valendo. */
export function sameStudio(a: StudioChoice, b: StudioChoice): boolean {
  const key = (s: StudioChoice) =>
    JSON.stringify([
      s.layout.id,
      resolvedStudio(s),
      storedStyleOf(s).accent,
      s.palette,
      s.mobileNav ?? 'drawer',
    ]);
  return key(a) === key(b);
}

export interface StudioReview {
  theme: ThemeFile;
  /** Pares de cor com pouco contraste (texto que fica difícil de ler). */
  warnings: ContrastWarning[];
  /** Ajustes que diferem do padrão do layout ou do acabamento, para o resumo. */
  changedLayout: LayoutOptionKey[];
  changedFinish: FinishOptionKey[];
}

/** A conferência da etapa Revisar. */
export function reviewOf(s: StudioChoice, name: string): StudioReview {
  const theme = studioTheme(s, name) ?? studioTheme(BEADEV_STUDIO, name)!;
  const offered = new Set(offeredLayoutOptions(s.layout.id));
  const changedLayout = Object.keys(s.layout.options ?? {}).filter((k) =>
    offered.has(k as LayoutOptionKey),
  ) as LayoutOptionKey[];
  const base = resolvedFinish(s.finish.id) ?? resolvedFinish('sobrio')!;
  const now = resolvedStudio(s).finish;
  const changedFinish = STUDIO_FINISH_OPTIONS.filter((k) => now[k] !== base[k]);
  return {
    theme,
    warnings: themeContrastWarnings(theme).filter((w) => w.mode === 'light'),
    changedLayout,
    changedFinish,
  };
}

/** A chave de tradução de um valor de ajuste ("1.5" vira "1_5": o ponto separa as chaves). */
export const optionKey = (value: string): string => value.replace('.', '_');

/** A cor ajustável que muda cada texto da conferência (os outros saem do destaque ou da lateral). */
const FIXABLE: Partial<Record<ContrastWarning['text'], PaletteColorKey>> = {
  ink: 'ink',
  muted: 'muted',
  danger: 'danger',
  ok: 'ok',
  info: 'info',
  'sidebar-text': 'sidebarText',
};

/**
 * O conserto de um aviso da conferência (ADR 0133, parte 5): a cor do texto escurece (ou clareia,
 * sobre fundo escuro) até ler bem, como um ajuste de cor que se desfaz. Null quando o texto não é
 * uma cor ajustável: aí o conserto é trocar o destaque ou a paleta.
 */
export function fixOf(s: StudioChoice, w: ContrastWarning): StudioChoice | null {
  const key = FIXABLE[w.text];
  if (!key) return null;
  const c = reviewOf(s, '').theme.colors[w.mode];
  const text = c[w.text];
  const bg = c[w.background];
  if (!text || !bg) return null;
  return withColor(s, key, readableOn(text, [bg], luminance(bg) < 0.4, w.minimum));
}
